
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { makeNameQuery } from "@/helpers/queryHelperFunctions";

import LIST_COMPANY_REQUESTS from "@/apollo/jobboard/ListCompanyRequests.gql";
import LIST_CONTRACTORS from "@/apollo/contractors/ListContractors.gql";
import TotalCounter from "@/components/common/TotalCounter.vue";
import ArchivedRequestContractorCard from "@/components/onboarding/ArchivedRequestContractorCardNew.vue";
import RequestCard from "@/components/onboarding/RequestCardResourcesNew.vue";
import CardPagination from "@/components/card/PaginationNew.vue";
import ItemChip from "@/components/common/ItemChipCustom.vue";
import FormGroup from "@/components/form/FormGroupNew.vue";
import RadioButtonGroup from "@/components/common/RadioButtonGroupNew.vue";
import UiButton from "@/components/button/ui-button.vue";
import Processing from "@/components/common/Processing.vue";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import InputFilter from "@/components/filters/InputFilter.vue";
import CardBodyItem from "@/components/card/CardBodyItem.vue";
import ActionPanel from "@/components/common/ActionsPanel.vue";
import { hslColorFromName } from "@/utils/hslColorFromName";
import RouteNames from "@/entities/RouteNames";
import paginationConfig from "@/data/paginationConfig";
import PaginationConfig from "@/entities/PaginationConfig";
import RouteParams from "@/entities/RouteParams";
import {
  User,
  Company,
  Manager_Contractor_Bool_Exp,
  Contractor_Bool_Exp,
} from "@/generated/graphql";
import { INVITATION } from "@/entities/Events";
import RadioOption from "@/entities/RadioOptionNew";
import Roles from "@/entities/Roles";
import UserIcon from "@/components/icons/user.vue";
import { getEmptyFiltersResultMessage } from "@/helpers/lists/emptyListMessages";
import EmptyListImageRequests from "@/components/icons/emptyListImages/EmptyListImageRequests.vue";
import commonContractorRequestConditions from "@/data/contractorReguests/commonContractorRequestConditions";
import { Contractor_status } from "@/data/contractorsStatus";

@Component({
  components: {
    EmptyListImageRequests,
    ArchivedRequestContractorCard,
    RequestCard,
    UserIcon,
    CardPagination,
    ItemChip,
    FormGroup,
    RadioButtonGroup,
    UiButton,
    TotalCounter,
    Processing,
    ActionPanel,
    EmptyListPlaceholder,
    LoadingOverlay,
    InputFilter,
    CardBodyItem,
  },
  apollo: {
    requests: {
      query: LIST_COMPANY_REQUESTS,
      variables() {
        return this.requestsFilter;
      },
      update(data) {
        this.requestCardsConfig.total = data.total.aggregate.count;
        this.requestCardsConfig.loadedCount = data.requests.length;
        this.activeTotal = data.total.aggregate.count;
        return data.requests;
      },
    },
    archivedContractors: {
      query: LIST_CONTRACTORS,
      variables() {
        return this.archivedFilter;
      },
      update(data) {
        this.archivedRequestsConfig.total = data.total.aggregate.count;
        this.archivedRequestsConfig.loadedCount = data.contractor.length;
        this.archiveTotal = data.total.aggregate.count;
        return data.contractor;
      },
    },
  },
})
export default class Requests extends Vue {
  @Prop({ type: Object, required: true }) readonly company!: Company;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;

  requests: any[] | null = null;
  archivedContractors: any[] | null = null;
  nameValue: string = "";
  updatedProcessing: boolean = false;
  routes = RouteNames;
  activeTotal: number = 0;
  archiveTotal: number = 0;

  requestCardsConfig: PaginationConfig = {
    ...paginationConfig,
  };

  archivedRequestsConfig: PaginationConfig = {
    ...paginationConfig,
  };

  activeRequests: boolean = true;
  requestsStatus: string = "active";

  @Watch("requestsStatus")
  onTabChange(val: string) {
    if (val == "active") {
      this.$apollo.queries.requests.refetch();
      this.archivedContractors = null;
      this.nameValue = "";
    }
    if (val == "archived") {
      this.requests = null;
      this.nameValue = "";
    }
  }

requestCreatorLink(request): RouteParams<{ id: string }> | undefined {
    const managerId = request.manager.id;
    if (!managerId) {
      return undefined;
    }
    return {
      name: RouteNames.MANAGER_ABOUT,
      params: { id: managerId },
    };
  }

  created(): void {
    this.$eventBus.listen(INVITATION, this.refetch);
  }

  beforeDestroy(): void {
    this.$eventBus.remove(INVITATION, this.refetch);
  }

  get radioOptions() {
    return [
      {
        value: "active",
        label: "Active",
        total: this.activeTotal,
      },
      {
        value: "archived",
        label: "Archived",
        total: this.archiveTotal,
      },
    ];
  }

  get emptyListPlaceholder(): string {
    if (this.nameValue.length > 0) {
      return getEmptyFiltersResultMessage("requests");
    }

    return "There are no pending requests at the moment";
  }

  get getTotal(): number {
    if (this.isAdmin) return this.activeTotal + this.archiveTotal;
    if (!this.isAdmin) return this.activeTotal;
  }

  get skipQuery(): boolean {
    return this.isActiveRequests;
  }

  get isResourcesPage(): boolean {
    return this.$route.path.includes("/company_database");
  }

  get loading(): boolean {
    return this.requestsStatus === "active"
      ? this.$apollo.queries.requests.loading
      : this.$apollo.queries.archivedContractors.loading;
  }

  get hasData(): boolean {
    if (this.requestsStatus === "active") {
      return !!this.requests;
    } else if (this.requestsStatus === "archived") {
      return !!this.archivedContractors;
    }

    return false;
  }

  get emptyActive(): boolean {
    if (!this.requests) {
      return false;
    }

    return !this.$apollo.queries.requests.loading && !this.requests?.length;
  }

  get emptyArchived(): boolean {
    if (!this.archivedContractors && this.archivedContractors?.length > 0) {
      return false;
    }

    return (
      !this.$apollo.queries.archivedContractors.loading &&
      !this.archivedContractors?.length
    );
  }

  get showArchivedPagination(): boolean {
    return !!this.archivedContractors?.length && !this.isActiveRequests;
  }

  get showActivePagination(): boolean {
    return !!this.requests?.length && this.isActiveRequests;
  }

  get isAdmin(): boolean {
    return this.currentUser.role == Roles.ADMIN;
  }

  get archivedCardsForAdmin(): boolean {
    return !this.isActiveRequests && this.isAdmin;
  }

  get isActiveRequests(): boolean {
    return this.requestsStatus == "active";
  }

  get showRequestedBy(): boolean {
    return this.isAdmin;
  }

  get requestsFilter(): any {
    const { page, limit } = this.requestCardsConfig;
    const nextOffset = page * limit - limit;
    const conditions: Manager_Contractor_Bool_Exp[] = [
      ...commonContractorRequestConditions,
    ];
    if (this.nameValue && (this.requestsStatus == "active" || !this.isAdmin)) {
      conditions.push(makeNameQuery(this.nameValue, ["contractor", "user"]));
    }

    return {
      offset: nextOffset,
      limit: limit,
      where: {
        _and: conditions,
      },
      orderBy: [{ updated_at: "desc" }],
    };
  }

  get archivedFilter(): any {
    const { page, limit } = this.archivedRequestsConfig;
    const nextOffset = page * limit - limit;
    const conditions: Contractor_Bool_Exp[] = [
      {
        deleted: { _eq: true },
      },
      // {
      //   status: { _neq: Contractor_status.SUSPENDED },
      // },
    ];

    if (this.nameValue && this.requestsStatus == "archived") {
      conditions.push(makeNameQuery(this.nameValue, ["user"]));
    }

    return {
      offset: nextOffset,
      limit: limit,
      where: {
        _and: conditions,
      },
      orderBy: [{ updated_at: "desc" }],
    };
  }

  getFullName(user: User): string {
    const name = user.given_name?.trim();
    const surname = user.family_name?.trim();
    return `${name} ${surname}`;
  }

  refetch(): void {
    this.$apollo.queries.requests.refetch();
    this.$emit("refetchRequestsContractorsNumber");
  }

  async handleArchivedRefetch(): Promise<any> {
    this.$apollo.queries.archivedContractors.refetch();
  }
}
